'use client';

import { c as _c } from "react/compiler-runtime";
import { useUser } from '@auth0/nextjs-auth0';
import { Heart, HeartSolid, WarningSolid } from '@byway/icons';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { Button } from '~/components/Button';
import { SaveTripDialog } from '~/components/Common/SaveButton/src/SaveTripDialog';
import { SimpleSpinner } from '~/components/Spinner';
import { usePlaygroundCurrentSkeletonTrip } from '~/stores/playgroundStore';
import styles from './SaveTripButton.module.css';
import { useBuildThenSave } from './useBuildThenSave';
export const SaveTripButton = () => {
  const $ = _c(41);
  const trip = usePlaygroundCurrentSkeletonTrip();
  const {
    user
  } = useUser();
  const tAccount = useTranslations("account");
  const tPlayground = useTranslations("playground");
  const {
    save,
    unsave,
    deferSave,
    isBuilding,
    isSaving,
    isSaved,
    error,
    clearError
  } = useBuildThenSave();
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);
  const tripId = trip?.uniqueId;
  const previousTripIdRef = useRef(tripId);
  let t0;
  let t1;
  if ($[0] !== clearError || $[1] !== tripId) {
    t0 = () => {
      if (previousTripIdRef.current !== tripId) {
        previousTripIdRef.current = tripId;
        clearError();
      }
    };
    t1 = [tripId, clearError];
    $[0] = clearError;
    $[1] = tripId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  if (process.env.NEXT_PUBLIC_FF_ENABLE_SAVE_IN_MAPS !== "true" || !trip) {
    return null;
  }
  const isBusy = isBuilding || isSaving;
  let t2;
  if ($[4] !== isBusy || $[5] !== isSaved || $[6] !== save || $[7] !== unsave || $[8] !== user) {
    t2 = () => {
      if (isBusy) {
        return;
      }
      if (!user) {
        setIsLoginDialogOpen(true);
        return;
      }
      if (isSaved) {
        unsave();
      } else {
        save();
      }
    };
    $[4] = isBusy;
    $[5] = isSaved;
    $[6] = save;
    $[7] = unsave;
    $[8] = user;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handlePress = t2;
  let t3;
  if ($[10] !== deferSave) {
    t3 = async () => {
      await deferSave();
      setIsLoginDialogOpen(false);
    };
    $[10] = deferSave;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleLogin = t3;
  let t4;
  if ($[12] !== isBusy) {
    t4 = clsx(styles.label, {
      [styles.saving]: isBusy
    });
    $[12] = isBusy;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== isBusy || $[15] !== isSaved || $[16] !== tAccount || $[17] !== tPlayground) {
    t5 = isBusy ? <><SimpleSpinner aria-hidden={true} data-test-id="save-trip-spinner" />{tPlayground("saveTrip.saving")}</> : <>{isSaved ? <HeartSolid decorative={true} size="large" dataTestId="save-trip-icon-saved" /> : <Heart decorative={true} size="large" dataTestId="save-trip-icon-unsaved" />}{isSaved ? tAccount("saved") : tAccount("save")}</>;
    $[14] = isBusy;
    $[15] = isSaved;
    $[16] = tAccount;
    $[17] = tPlayground;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t4 || $[20] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== handlePress || $[23] !== isBusy || $[24] !== t6) {
    t7 = <Button variant="bare" dataTestId="save-trip-button" isPending={isBusy} onPress={handlePress}>{t6}</Button>;
    $[22] = handlePress;
    $[23] = isBusy;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== isBusy || $[27] !== t7) {
    t8 = <div className={styles.buttonRow} data-test-id="save-trip-button-row" aria-busy={isBusy}>{t7}</div>;
    $[26] = isBusy;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== error || $[30] !== tPlayground) {
    t9 = error && <p role="alert" className={styles.error}><span className={styles.errorIcon}><WarningSolid decorative={true} dataTestId="save-trip-error-icon" /></span>{tPlayground("saveTrip.build_error")}</p>;
    $[29] = error;
    $[30] = tPlayground;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setIsLoginDialogOpen(false);
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setIsLoginDialogOpen(false);
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== handleLogin || $[35] !== isLoginDialogOpen) {
    t12 = <SaveTripDialog isLoggedIn={false} isOpen={isLoginDialogOpen} onClose={t10} onLogin={handleLogin} onGoToAccount={t11} />;
    $[34] = handleLogin;
    $[35] = isLoginDialogOpen;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t12 || $[38] !== t8 || $[39] !== t9) {
    t13 = <div className={styles.container}>{t8}{t9}{t12}</div>;
    $[37] = t12;
    $[38] = t8;
    $[39] = t9;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  return t13;
};